import {
  Box,
  Divider,
  Flex,
  Link,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  Text,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react';
import { ReactComponent as ArrowDownBlack } from 'assets/arrow-down-black-icon.svg';
import { ReactComponent as HashpackLogo } from 'assets/hashpack-wallet-logo.svg';
import { ReactComponent as WalletconnectIcon } from 'assets/walletconnect-logo.svg';
import { ReactComponent as GreenWalletIcon } from 'assets/wallet-icon-green.svg';
import { ReactComponent as CopyIcon } from 'assets/copy-icon.svg';
import { ReactComponent as ExternalIcon } from 'assets/external-icon.svg';
import { ReactComponent as AddressBookIcon } from 'assets/address-book-icon.svg';
import { ReactComponent as SettingsIcon } from 'assets/settings-icon.svg';
import { ReactComponent as HelpIcon } from 'assets/help-icon.svg';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from '../../providers/Global';
import { getAccountData } from '../../api/userAPI';
import { HASHPACK_DOWNLOAD_URL, ModalType } from '../../utils/constants';
import { useToast } from '../../hooks/useToast';
import copyToClipboard from 'copy-to-clipboard';
import { styles } from './styles';
import { getExplorerUrl } from '../../utils/explorerUrl';
import { useSigningConfirmation } from '../../providers/SigningConfirmation';
import SigningConfirmationContent from '../Modals/SigningConfirmationModal';

type TPopoverItems = {
  logo: JSX.Element;
  text: string;
  description?: string;
  clickHandler: () => void;
  isDisabled: boolean;
}

const PopoverItems = () => {
  const {
    connection: {
      connectToHashPack,
      connectViaWalletConnect,
      isHashPackExtensionAvailable,
    }
  } = useContext(GlobalContext);
  const { onClose } = useDisclosure();

  const handleHashPackConnection = async () => {
    if (isHashPackExtensionAvailable) {
      onClose();
      connectToHashPack();
    } else {
      window.open(HASHPACK_DOWNLOAD_URL, '_blank', 'noopener');
    }
  };

  const handleWalletConnectConnection = async () => {
    onClose();
    connectViaWalletConnect();
  };

  const popoverItems: TPopoverItems[] = [
    {
      text: 'HashPack',
      description: isHashPackExtensionAvailable ? 'Browser Extension' : 'Install Extension',
      logo: <HashpackLogo style={{ width: '32px', height: '32px' }} />,
      clickHandler: handleHashPackConnection,
      isDisabled: false
    },
    {
      text: 'WalletConnect',
      logo: <WalletconnectIcon style={{ width: '32px', height: '32px', flexShrink: 0 }} />,
      clickHandler: handleWalletConnectConnection,
      isDisabled: false
    },
  ];

  return (
    <Flex flexDirection="column" gap={2}>
      {popoverItems.map((item, idx) => {
        if (!item.isDisabled) {
          return (
            <Box
              key={`wallet-widget-x${idx}`}
              sx={styles.popoverItem}
              onClick={item.clickHandler}
            >
              {item.logo}
              <Flex flexDirection="column" flex={1}>
                <Text size="sm" fontWeight={600}>
                  {item.text}
                </Text>
                {item.description && (
                  <Text size="xs" color="gray.500">
                    {item.description}
                  </Text>
                )}
              </Flex>
            </Box>
          );
        }
        return null;
      })}
    </Flex>
  );
};

const WalletsWidget = () => {
  const {
    connection: { accountId, disconnectWallet },
    connectedNetwork,
    openModal,
  } = useContext(GlobalContext);
  const { onOpen, onClose, isOpen } = useDisclosure();
  const flexRef = useRef(null);
  const { showToast } = useToast();
  const { pendingRequest } = useSigningConfirmation();
  const isSigningOpen = !!pendingRequest;

  const [hbarBalance, setHbarBalance] = useState<number | null>(null);
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);

  useOutsideClick({
    ref: flexRef,
    handler: onClose
  });

  const fetchBalance = useCallback(async () => {
    if (!accountId || !connectedNetwork) return;
    // Skip if custom network and not yet authenticated (no JWT token)
    if (connectedNetwork.startsWith('custom-') && !localStorage.getItem('token')) return;
    setIsLoadingBalance(true);
    try {
      const { balance } = await getAccountData(accountId, connectedNetwork);
      setHbarBalance(balance);
    } catch (error: unknown) {
      console.error('Failed to fetch account balance:', error);
    } finally {
      setIsLoadingBalance(false);
    }
  }, [accountId, connectedNetwork]);

  useEffect(() => {
    if (isOpen && accountId) {
      fetchBalance();
    }
  }, [isOpen, accountId, fetchBalance]);

  const handleCopyAddress = () => {
    if (!accountId) return;
    copyToClipboard(accountId);
    showToast('Address copied to clipboard', 'success', undefined, 'top');
  };

  const handleMenuItem = (action: () => void) => {
    onClose();
    action();
  };

  // Force popover open when signing request is pending
  const popoverOpen = isSigningOpen || isOpen;

  return (
    <>
    {/* Signing overlay: dims everything and hides any open Chakra modals */}
    {isSigningOpen && (
      <>
        <style>{`
          .chakra-modal__overlay, .chakra-modal__content-container {
            display: none !important;
          }
        `}</style>
        <Box
          position="fixed"
          top="64px"
          left={0}
          right={0}
          bottom={0}
          bg="blackAlpha.400"
          backdropFilter="blur(6px)"
          zIndex={1399}
          onClick={pendingRequest?.onCancel}
        />
      </>
    )}
    <Popover isOpen={popoverOpen}>
      <PopoverTrigger>
        <Flex
          ref={flexRef}
          onClick={isSigningOpen ? undefined : (isOpen ? onClose : onOpen)}
          gap={2}
          w="fit-content"
          alignItems="center"
          cursor={isSigningOpen ? 'default' : 'pointer'}
          position="relative"
          zIndex={isSigningOpen ? 1400 : undefined}
        >
          <GreenWalletIcon style={{ maxHeight: '32px', maxWidth: '24px' }} />
          <Text mt="2px" size="sm" variant="bodyBold" mr="auto">
            {accountId ? accountId : 'Connect Wallet'}
          </Text>
          <ArrowDownBlack style={styles.rotatingArrow(isOpen)} />
        </Flex>
      </PopoverTrigger>
      <PopoverContent
        p={0}
        mt={5}
        w={isSigningOpen ? 320 : 280}
        borderRadius={16}
        boxShadow={isSigningOpen ? '0px 8px 32px rgba(0, 0, 0, 0.2)' : '0px 4px 16px 0px rgba(0, 0, 0, 0.12)'}
        _focusVisible={{ outline: 'none' }}
        zIndex={isSigningOpen ? 1401 : undefined}
        sx={isSigningOpen ? {
          '@keyframes signingPulse': {
            '0%, 100%': { boxShadow: '0 0 0 0 rgba(30, 200, 67, 0), 0px 8px 32px rgba(0, 0, 0, 0.2)' },
            '50%': { boxShadow: '0 0 18px 6px rgba(30, 200, 67, 0.3), 0px 8px 32px rgba(0, 0, 0, 0.2)' },
          },
          animation: 'signingPulse 2s ease-in-out infinite',
        } : undefined}
      >
        <PopoverBody p={0}>
          {isSigningOpen && pendingRequest ? (
            <SigningConfirmationContent
              details={pendingRequest.details}
              onConfirm={pendingRequest.onConfirm}
              onCancel={pendingRequest.onCancel}
            />
          ) : accountId ? (
            <Flex flexDirection="column">
              {/* Account header: ID + Balance */}
              <Box px={4} pt={4} pb={3}>
                <Text size="sm" variant="bodyBold" isTruncated>
                  {accountId}
                </Text>
                <Flex alignItems="center" gap={1} mt={1}>
                  <Text fontSize="lg" fontWeight={700}>
                    {isLoadingBalance ? (
                      <Skeleton as="span" display="inline-block" w="60px" h="20px" borderRadius={4} />
                    ) : (
                      `\u210F ${hbarBalance !== null ? hbarBalance.toFixed(2) : '\u2014'}`
                    )}
                  </Text>
                </Flex>

                {/* Copy + HashScan action icons */}
                <Flex gap={1} mt={2}>
                  <Box
                    as="button"
                    aria-label="Copy address"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    p={1.5}
                    borderRadius={6}
                    transition="background 0.15s"
                    _hover={{ bg: 'blackAlpha.100' }}
                    onClick={handleCopyAddress}
                  >
                    <CopyIcon style={{ width: 16, height: 16 }} />
                  </Box>
                  {getExplorerUrl(connectedNetwork!, 'account', accountId!) && (
                    <Link
                      href={getExplorerUrl(connectedNetwork!, 'account', accountId!)!}
                      isExternal
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      p={1.5}
                      borderRadius={6}
                      title="View in Explorer"
                      transition="background 0.15s"
                      _hover={{ bg: 'blackAlpha.100' }}
                    >
                      <ExternalIcon style={{ width: 16, height: 16 }} />
                    </Link>
                  )}
                </Flex>
              </Box>

              <Divider borderColor="blackAlpha.200" />

              {/* Menu items */}
              <Flex flexDirection="column" py={2} px={2}>
                <Box
                  as="button"
                  sx={styles.menuItem}
                  onClick={() => handleMenuItem(() => openModal(ModalType.AddressBookDrawer))}
                >
                  <AddressBookIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
                  <Text size="sm" fontWeight={500}>Address Book</Text>
                </Box>
                <Box
                  as="button"
                  sx={styles.menuItem}
                  onClick={() => handleMenuItem(() => openModal(ModalType.NotificationSettingsDrawer))}
                >
                  <SettingsIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
                  <Text size="sm" fontWeight={500}>Notification Settings</Text>
                </Box>
                <Box
                  as="button"
                  sx={styles.menuItem}
                  onClick={() => handleMenuItem(() => openModal(ModalType.FAQDrawer))}
                >
                  <HelpIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
                  <Text size="sm" fontWeight={500}>Help & FAQs</Text>
                </Box>
              </Flex>

              <Divider borderColor="blackAlpha.200" />

              {/* Disconnect */}
              <Box px={3} py={3}>
                <Box
                  as="button"
                  sx={styles.menuItem}
                  color="red.500"
                  onClick={() => handleMenuItem(disconnectWallet)}
                >
                  <Text size="sm" fontWeight={500}>Disconnect</Text>
                </Box>
              </Box>
            </Flex>
          ) : <Box p={3}><PopoverItems /></Box>}
        </PopoverBody>
      </PopoverContent>
    </Popover>
    </>
  );
};

export default WalletsWidget;
